import { useState, useCallback } from "react";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL, ACTOR_ID_HEADER_KEY, O_CLIENT_APP, O_API_KEY, STORAGE_BASE_URL } from "constants";
import { useAuth } from "contexts/AuthContext";
import useFilters from "hooks/useFilters";

const useTasks = (projectId = null) => {
  const { user } = useAuth();

  const [state, setState] = useState({
    tasks: [],
    tasksByIteration: {},
    tasksByProject: {},
    loading: false,
    error: null,
  });

  const buildHeaders = useCallback(() => {
    const headers = {
      "O-CLIENT-APP": O_CLIENT_APP,
      "O-API-KEY": O_API_KEY,
    };
    if (user?.id) {
      headers["X-User-Id"] = user.id;
    }
    return headers;
  }, [user?.id]);

  const { selectedTagIds } = useFilters();

  /** Generic GET helper **/
  const getRequest = useCallback(async (url) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || err.message || "An unexpected error occurred";
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic POST helper **/
  const postRequest = useCallback(async (url, payload, config = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.post(url, payload, config);
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || err.message || "An unexpected error occurred";
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic PUT helper **/
  const putRequest = useCallback(async (url, payload, config = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.put(url, payload, config);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic PATCH helper **/
  const patchRequest = useCallback(async (url, payload, config = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.patch(url, payload, config);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic DELETE helper **/
  const deleteRequest = useCallback(async (url, config = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await apiClient.delete(url, config);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Fetch tasks for a specific iteration **/
  const fetchTasksByIteration = useCallback(
    async (projectIdToFetch, iterationIdToFetch) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectIdToFetch}/tasks?iterationId=${iterationIdToFetch}&size=1000`;
        const data = await getRequest(url);
        const tasksList = data.content ? data.content : Array.isArray(data) ? data : [data];

        setState((prev) => ({
          ...prev,
          tasksByIteration: {
            ...prev.tasksByIteration,
            [iterationIdToFetch]: tasksList,
          },
          loading: false,
        }));

        return tasksList;
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err.message || "Failed to fetch tasks",
        }));
        throw err;
      }
    },
    [getRequest]
  );

  /** Fetch tasks for all iterations in a project **/
  const fetchAllTasksForIterations = useCallback(async (projectIdToFetch, iterationIds) => {
    if (!iterationIds || iterationIds.length === 0) {
      return {};
    }

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const promises = iterationIds.map((iterId) =>
        apiClient
          .get(`${PM_SERVICE_BASE_URL}/api/v1/projects/${projectIdToFetch}/tasks?iterationId=${iterId}&size=1000`)
          .then((res) => {
            const data = res.data;
            const tasks = data.content ? data.content : Array.isArray(data) ? data : [];
            return { iterationId: iterId, tasks };
          })
          .catch((err) => {
            console.error(`[useTasks] Failed to fetch tasks for iteration ${iterId}:`, err);
            return { iterationId: iterId, tasks: [] };
          })
      );

      const results = await Promise.all(promises);

      const tasksMap = {};
      results.forEach(({ iterationId, tasks }) => {
        tasksMap[iterationId] = tasks;
      });

      setState((prev) => ({
        ...prev,
        tasksByIteration: { ...prev.tasksByIteration, ...tasksMap },
        loading: false,
      }));

      return tasksMap;
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Failed to fetch tasks",
      }));
      throw err;
    }
  }, []);

  /** Fetch tasks for a project **/
  const fetchTasksByProject = useCallback(
    async (projectIdToFetch) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectIdToFetch}/tasks?size=1000`;
        const data = await getRequest(url);
        const tasksList = data.content ? data.content : Array.isArray(data) ? data : [data];

        setState((prev) => ({
          ...prev,
          tasksByProject: {
            ...prev.tasksByProject,
            [projectIdToFetch]: tasksList,
          },
          loading: false,
        }));

        return tasksList;
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err.message || "Failed to fetch tasks",
        }));
        throw err;
      }
    },
    [getRequest]
  );

  /** Create task **/
  const createTask = useCallback(
    async (projectId, taskData) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks`;
        const headers = {};
        if (user?.id) {
          headers[ACTOR_ID_HEADER_KEY] = user.id;
        }
        const response = await apiClient.post(url, taskData, {
          ...(Object.keys(headers).length > 0 && { headers }),
        });
        const data = response.data;

        if (data.iterationId) {
          setState((prev) => ({
            ...prev,
            tasksByIteration: {
              ...prev.tasksByIteration,
              [data.iterationId]: [...(prev.tasksByIteration[data.iterationId] || []), data],
            },
          }));
        }

        setState((prev) => ({
          ...prev,
          tasksByProject: {
            ...prev.tasksByProject,
            [projectId]: [...(prev.tasksByProject[projectId] || []), data],
          },
          loading: false,
        }));

        return data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
        throw new Error(errorMessage);
      }
    },
    [user?.id]
  );

  const buildCloneTaskPayload = useCallback((sourceTask = {}, overrides = {}) => {
    const tagIds = Array.isArray(sourceTask.tags)
      ? sourceTask.tags.map((tag) => tag?.id).filter(Boolean)
      : [];
    const textFiles = Array.isArray(sourceTask.textFiles)
      ? sourceTask.textFiles
          .map((file) => ({
            fileId: file?.fileId,
            width: Number(file?.width) > 0 ? Number(file.width) : 1,
            height: Number(file?.height) > 0 ? Number(file.height) : 1,
          }))
          .filter((file) => file.fileId)
      : [];
    const assigneeId =
      sourceTask.assigneeId ||
      sourceTask.assignee?.id ||
      sourceTask.assignedUser?.id ||
      sourceTask.assignedTo?.id ||
      null;

    const payload = {
      type: sourceTask.type || "TASK",
      title: sourceTask.title || "",
      description: sourceTask.description || null,
      acceptanceCriteria: sourceTask.acceptanceCriteria || null,
      status: sourceTask.status || "OPEN",
      priority: sourceTask.priority ?? 2,
      points: sourceTask.points ?? 1,
      assignmentStatus: sourceTask.assignmentStatus || null,
      iterationId: sourceTask.iterationId || sourceTask.iteration?.id || null,
      startedAt: sourceTask.startedAt || null,
      expectedCompletionDate: sourceTask.expectedCompletionDate || null,
      actualCompletionDate: sourceTask.actualCompletionDate || null,
      comment: sourceTask.comment || "",
      tagIds,
      textFiles,
      ...(assigneeId ? { assigneeId } : {}),
    };

    // Preserve dependency-shaped fields if they are already supplied on the task DTO.
    [
      "dependencyIds",
      "dependencies",
      "dependsOn",
      "blockedBy",
      "predecessorIds",
      "successorIds",
      "relatedTaskIds",
      "parentTaskId",
    ].forEach((field) => {
      if (sourceTask[field] !== undefined) {
        payload[field] = sourceTask[field];
      }
    });

    return {
      ...payload,
      ...overrides,
    };
  }, []);

  /** Update task **/
  const updateTask = useCallback(
    async (projectId, taskId, taskData) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}`;
        const headers = {};
        if (user?.id) {
          headers[ACTOR_ID_HEADER_KEY] = user.id;
        }
        const response = await apiClient.put(url, taskData, {
          ...(Object.keys(headers).length > 0 && { headers }),
        });
        const data = response.data;

        if (data.iterationId) {
          setState((prev) => ({
            ...prev,
            tasksByIteration: {
              ...prev.tasksByIteration,
              [data.iterationId]: (prev.tasksByIteration[data.iterationId] || []).map((task) =>
                task.id === taskId ? data : task
              ),
            },
          }));
        }

        setState((prev) => ({
          ...prev,
          tasksByProject: {
            ...prev.tasksByProject,
            [projectId]: (prev.tasksByProject[projectId] || []).map((task) =>
              task.id === taskId ? data : task
            ),
          },
          loading: false,
        }));

        return data;
      } catch (err) {
        throw err;
      }
    },
    [user?.id]
  );

  /** Delete task **/
  const deleteTask = useCallback(
    async (projectId, taskId) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}`;
        await deleteRequest(url);

        setState((prev) => {
          const newTasksByIteration = { ...prev.tasksByIteration };
          Object.keys(newTasksByIteration).forEach((iterId) => {
            newTasksByIteration[iterId] = newTasksByIteration[iterId].filter((task) => task.id !== taskId);
          });
          return { ...prev, tasksByIteration: newTasksByIteration };
        });

        setState((prev) => ({
          ...prev,
          tasksByProject: {
            ...prev.tasksByProject,
            [projectId]: (prev.tasksByProject[projectId] || []).filter((task) => task.id !== taskId),
          },
          loading: false,
        }));
      } catch (err) {
        throw err;
      }
    },
    [deleteRequest]
  );

  // ==================== TASK COMMENTS ====================

  /** Fetch comments for a task **/
  /** GET: /api/v1/projects/{projectId}/tasks/{taskId}/comments **/
  const fetchTaskComments = useCallback(async (projectId, taskId) => {
    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/comments?sort=createdAt,desc&page=0`;
      const response = await apiClient.get(url, { headers: buildHeaders() });
      const data = response.data;
      const comments = data.content ? data.content : (Array.isArray(data) ? data : []);
      setState((prev) => ({ ...prev, loading: false }));
      return comments;
    } catch (err) {
      console.error("[useTasks:fetchTaskComments]", err);
      setState((prev) => ({ ...prev, loading: false }));
      throw err;
    }
  }, [buildHeaders]);

  /** Add a comment to a task **/
  const addTaskComment = useCallback(
    async (projectId, taskId, commentData) => {
      const { id, email, firstName, lastName, username } = user || {};
      const authToken = sessionStorage.getItem("authToken");

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/comments`;
        const headers = {};
        if (id) {
          headers["X-User-Id"] = id;
          const userName = `${firstName || ""} ${lastName || ""}`.trim() || email || username || String(id);
          headers["X-User-Name"] = userName;
        }
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        const data = await postRequest(url, commentData, { headers });
        setState((prev) => ({ ...prev, loading: false }));
        return data;
      } catch (err) {
        throw err;
      }
    },
    [postRequest, user?.id, user?.email, user?.firstName, user?.lastName, user?.username]
  );

  /** Update a comment **/
  const updateTaskComment = useCallback(
    async (projectId, taskId, commentId, commentData) => {
      const { id, email, firstName, lastName, username } = user || {};
      const authToken = sessionStorage.getItem("authToken");

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/comments/${commentId}`;
        const headers = {};
        if (id) {
          headers["X-User-Id"] = id;
          const userName = `${firstName || ""} ${lastName || ""}`.trim() || email || username || String(id);
          headers["X-User-Name"] = userName;
        }
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        const data = await putRequest(url, commentData, { headers });
        setState((prev) => ({ ...prev, loading: false }));
        return data;
      } catch (err) {
        throw err;
      }
    },
    [putRequest, user?.id, user?.email, user?.firstName, user?.lastName, user?.username]
  );

  /** Delete a comment **/
  const deleteTaskComment = useCallback(
    async (projectId, taskId, commentId) => {
      const { id, email, firstName, lastName, username } = user || {};

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/comments/${commentId}`;
        const headers = {};
        if (id) {
          headers["X-User-Id"] = id;
          const userName = `${firstName || ""} ${lastName || ""}`.trim() || email || username || String(id);
          headers["X-User-Name"] = userName;
        }

        await deleteRequest(url, { headers });
        setState((prev) => ({ ...prev, loading: false }));
      } catch (err) {
        throw err;
      }
    },
    [deleteRequest, user?.id, user?.email, user?.firstName, user?.lastName, user?.username]
  );

  // ==================== TASK LINKS ====================

  /** Fetch links for a task **/
  const fetchTaskLinks = useCallback(
    async (taskId) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${taskId}/links`;
        const response = await apiClient.get(url, {
          headers: buildHeaders(),
        });

        if (Array.isArray(response.data)) {
          return response.data;
        }

        if (Array.isArray(response.data?.data)) {
          return response.data.data;
        }

        console.warn("[fetchTaskLinks] Unexpected response shape:", response.data);
        return [];
      } catch (err) {
        throw err;
      }
    },
    [buildHeaders]
  );

  /** Create link(s) **/
  const addTaskLink = useCallback(
    async (taskId, link) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${taskId}/links`;

        const payload = [
          {
            url: link.url,
            label: link.label ?? null,
          },
        ];
        const response = await apiClient.post(url, payload, {
          headers: buildHeaders(),
        });

        return response.data;
      } catch (err) {
        throw err;
      }
    },
    [buildHeaders]
  );

  /** Update a single link **/
  const updateTaskLink = useCallback(
    async (taskId, linkId, link) => {
      const headers = {};
      if (user?.id) {
        headers["X-User-Id"] = user.id;
      }

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${taskId}/links/${linkId}`;

        const payload = {
          url: link.url,
          label: link.label ?? null,
        };

        const response = await apiClient.put(url, payload, { headers });
        return response.data;
      } catch (err) {
        throw err;
      }
    },
    [user?.id]
  );

  /** Delete a single link **/
  const deleteTaskLink = useCallback(
    async (taskId, linkId) => {
      const headers = {};
      if (user?.id) {
        headers["X-User-Id"] = user.id;
      }

      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${taskId}/links/${linkId}`;
        await apiClient.delete(url, { headers });
      } catch (err) {
        throw err;
      }
    },
    [user?.id]
  );

  // ==================== TASK ATTACHMENTS ====================

  /** Fetch attachments for a task **/
  const fetchTaskAttachments = useCallback(async (projectId, taskId) => {
    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/attachments`;
      const response = await apiClient.get(url, {
        headers: buildHeaders(),
      });
      return response.data;
    } catch (err) {
      console.error("[useTasks:fetchTaskAttachments]", err);
      throw err;
    }
  }, [buildHeaders]);

  /** Add attachments to a task **/
  const addTaskAttachments = useCallback(async (projectId, taskId, attachmentsData) => {
    const headers = {};
    if (user?.id) {
      headers[ACTOR_ID_HEADER_KEY] = user.id;
    }

    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/attachments`;
      const response = await apiClient.post(url, attachmentsData, { headers });
      return response.data;
    } catch (err) {
      console.error("[useTasks:addTaskAttachments]", err);
      throw err;
    }
  }, [user?.id]);

  /** Get a single attachment **/
  const getTaskAttachment = useCallback(async (projectId, taskId, attachmentId) => {
    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`;
      const response = await apiClient.get(url, {
        headers: buildHeaders(),
      });
      return response.data;
    } catch (err) {
      console.error("[useTasks:getTaskAttachment]", err);
      throw err;
    }
  }, [buildHeaders]);

  /** Delete an attachment **/
  const deleteTaskAttachment = useCallback(async (projectId, taskId, attachmentId) => {
    const headers = {};
    if (user?.id) {
      headers[ACTOR_ID_HEADER_KEY] = user.id;
    }

    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`;
      const response = await apiClient.delete(url, { headers });
      return response.data;
    } catch (err) {
      console.error("[useTasks:deleteTaskAttachment]", err);
      throw err;
    }
  }, [user?.id]);

  /** Update an attachment (patch) **/
  const patchTaskAttachment = useCallback(async (projectId, taskId, attachmentId, request) => {
    const headers = {};
    if (user?.id) {
      headers[ACTOR_ID_HEADER_KEY] = user.id;
    }

    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`;
      const response = await apiClient.patch(url, request, { headers });
      return response.data;
    } catch (err) {
      console.error("[useTasks:patchTaskAttachment]", err);
      throw err;
    }
  }, [user?.id]);

  const cloneTask = useCallback(
    async (projectId, sourceTask, overrides = {}) => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }

      if (!sourceTask?.id) {
        throw new Error("Source task is required");
      }

      const payload = buildCloneTaskPayload(sourceTask, {
        title: overrides.title || `${sourceTask.title || "Untitled Task"} (Copy)`,
        ...overrides,
      });

      const cloneRequestHeaders = buildHeaders();
      const sourceTextFiles = Array.isArray(payload.textFiles) ? payload.textFiles : [];

      const extractFileIdFromPayload = (value, visited = new WeakSet()) => {
        if (!value) return null;
        if (typeof value === "string") {
          return /^[0-9a-fA-F-]{36}$/.test(value) ? value : null;
        }
        if (typeof value !== "object") return null;
        if (visited.has(value)) return null;
        visited.add(value);

        if (Array.isArray(value)) {
          for (const item of value) {
            const resolved = extractFileIdFromPayload(item, visited);
            if (resolved) return resolved;
          }
          return null;
        }

        for (const field of ["fileId", "file_id", "fid", "id"]) {
          const candidate = value?.[field];
          if (typeof candidate === "string" && candidate.trim()) {
            return candidate;
          }
        }

        for (const nested of Object.values(value)) {
          const resolved = extractFileIdFromPayload(nested, visited);
          if (resolved) return resolved;
        }

        return null;
      };

      const collectFileIdsFromPayload = (value, result = new Set(), visited = new WeakSet()) => {
        if (!value) return result;
        if (typeof value === "string") {
          if (/^[0-9a-fA-F-]{36}$/.test(value)) {
            result.add(value);
          }
          return result;
        }
        if (typeof value !== "object") return result;
        if (visited.has(value)) return result;
        visited.add(value);

        if (Array.isArray(value)) {
          value.forEach((item) => collectFileIdsFromPayload(item, result, visited));
          return result;
        }

        ["fileId", "file_id", "fid", "id"].forEach((field) => {
          const candidate = value?.[field];
          if (typeof candidate === "string" && candidate.trim()) {
            result.add(candidate);
          }
        });

        Object.values(value).forEach((nested) => collectFileIdsFromPayload(nested, result, visited));
        return result;
      };

      const cloneFileInStorage = async (sourceFileId) => {
        const response = await apiClient.post(
          `${STORAGE_BASE_URL}/api/v1/files/${sourceFileId}/clone`,
          {},
          { headers: cloneRequestHeaders },
        );
        const clonedPayload = response?.data;
        const resolvedFileId = extractFileIdFromPayload(clonedPayload);
        const knownFileIds = [...collectFileIdsFromPayload(clonedPayload)];
        const clonedFileId =
          (resolvedFileId && resolvedFileId !== sourceFileId ? resolvedFileId : null) ||
          knownFileIds.find((id) => id && id !== sourceFileId) ||
          resolvedFileId;
        if (!clonedFileId) {
          throw new Error(`[useTasks:cloneTask] Failed to resolve cloned file ID for ${sourceFileId}`);
        }
        return { clonedFileId, clonedPayload };
      };

      const replaceReferencedFileIds = (input = "", fileIdMap = new Map()) => {
        if (!input || !(fileIdMap instanceof Map) || fileIdMap.size === 0) {
          return input;
        }

        let output = input;
        fileIdMap.forEach((clonedId, sourceId) => {
          if (!sourceId || !clonedId || sourceId === clonedId) return;
          const escapedSourceId = sourceId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          output = output
            .replace(new RegExp(`file-service://${escapedSourceId}`, "g"), `file-service://${clonedId}`)
            .replace(new RegExp(`\\[\\s*${escapedSourceId}(\\s*,)`, "g"), `[${clonedId}$1`);
        });

        return output;
      };

      const inlineFileCloneMap = new Map();
      if (sourceTextFiles.length > 0) {
        const uniqueInlineFileIds = [...new Set(sourceTextFiles.map((file) => file?.fileId).filter(Boolean))];
        await Promise.all(
          uniqueInlineFileIds.map(async (sourceFileId) => {
            const { clonedFileId } = await cloneFileInStorage(sourceFileId);
            inlineFileCloneMap.set(sourceFileId, clonedFileId);
          }),
        );
      }

      const clonedTaskPayload = {
        ...payload,
        description: replaceReferencedFileIds(payload.description, inlineFileCloneMap),
        acceptanceCriteria: replaceReferencedFileIds(payload.acceptanceCriteria, inlineFileCloneMap),
        textFiles: sourceTextFiles.map((file) => ({
          ...file,
          fileId: inlineFileCloneMap.get(file.fileId) || file.fileId,
        })),
      };

      const clonedTask = await createTask(projectId, clonedTaskPayload);

      try {
        const [sourceLinks, sourceAttachments] = await Promise.all([
          fetchTaskLinks(sourceTask.id).catch(() => []),
          sourceTask.projectId
            ? fetchTaskAttachments(sourceTask.projectId, sourceTask.id).catch(() => [])
            : Promise.resolve([]),
        ]);

        if (Array.isArray(sourceLinks) && sourceLinks.length > 0) {
          const linksPayload = sourceLinks
            .map((link) => ({
              url: link?.url,
              label: link?.label ?? null,
            }))
            .filter((link) => link.url);

          if (linksPayload.length > 0) {
            await apiClient.post(
              `${PM_SERVICE_BASE_URL}/api/v1/links/tasks/${clonedTask.id}/links`,
              linksPayload,
              { headers: buildHeaders() },
            );
          }
        }

        if (Array.isArray(sourceAttachments) && sourceAttachments.length > 0) {
          const normalizedAttachments = sourceAttachments
            .map((attachment) => ({
              fileId: attachment?.fileId || attachment?.file_id,
              fileName:
                attachment?.fileName ||
                attachment?.filename ||
                attachment?.name ||
                attachment?.label ||
                "attachment",
              fileSize: attachment?.fileSize || attachment?.size || null,
              mimeType: attachment?.mimeType || attachment?.type || null,
              label: attachment?.label || null,
              url:
                attachment?.url ||
                attachment?.fileUrl ||
                attachment?.file_url ||
                attachment?.meta?.url ||
                null,
            }))
            .filter((attachment) => attachment.fileId);

          const attachmentFileCloneMap = new Map();
          if (normalizedAttachments.length > 0) {
            const uniqueAttachmentFileIds = [...new Set(normalizedAttachments.map((attachment) => attachment.fileId))];
            await Promise.all(
              uniqueAttachmentFileIds.map(async (sourceFileId) => {
                const { clonedFileId, clonedPayload } = await cloneFileInStorage(sourceFileId);
                attachmentFileCloneMap.set(sourceFileId, {
                  fileId: clonedFileId,
                  payload: clonedPayload,
                });
              }),
            );
          }

          const attachmentsPayload = normalizedAttachments.map((attachment) => {
            const cloneInfo = attachmentFileCloneMap.get(attachment.fileId);
            const clonedPayload = cloneInfo?.payload;
            const clonedUrl =
              clonedPayload?.url ||
              clonedPayload?.fileUrl ||
              clonedPayload?.file_url ||
              clonedPayload?.meta?.url ||
              null;

            return {
              ...attachment,
              fileId: cloneInfo?.fileId || attachment.fileId,
              url: clonedUrl || attachment.url || null,
            };
          });

          if (attachmentsPayload.length > 0) {
            await addTaskAttachments(projectId, clonedTask.id, attachmentsPayload);
          }
        }
      } catch (err) {
        console.error("[useTasks:cloneTask] Failed to clone related task resources", err);
        throw err;
      }

      return clonedTask;
    },
    [
      addTaskAttachments,
      buildCloneTaskPayload,
      buildHeaders,
      createTask,
      fetchTaskAttachments,
      fetchTaskLinks,
    ]
  );

  // Helper functions for filtering

  function ReactifyFilter(items = [], selectedTagIds = []) {
    if (!selectedTagIds || selectedTagIds.length === 0) return items;
    const setIds = new Set(selectedTagIds);
    return items.filter((it) => {
      const tags = it.tags || [];
      const tagIds = tags
        .map((t) => (typeof t === "string" ? t : t.id))
        .filter(Boolean);
      return tagIds.some((id) => setIds.has(id));
    });
  }

  function mapFilterTasks(map = {}, selectedTagIds = []) {
    if (!selectedTagIds || selectedTagIds.length === 0) return map;
    const out = {};
    Object.keys(map).forEach((k) => {
      out[k] = ReactifyFilter(map[k] || [], selectedTagIds);
    });
    return out;
  }

  return {
    tasks: ReactifyFilter(projectId ? state.tasksByProject[projectId] || [] : [], selectedTagIds),
    tasksByIteration: mapFilterTasks(state.tasksByIteration, selectedTagIds),
    tasksByProject: mapFilterTasks(state.tasksByProject, selectedTagIds),
    loading: state.loading,
    error: state.error,
    fetchTasksByIteration,
    fetchAllTasksForIterations,
    fetchTasksByProject,
    createTask,
    cloneTask,
    updateTask,
    deleteTask,
    // Comments
    fetchTaskComments,
    addTaskComment,
    updateTaskComment,
    deleteTaskComment,
    // Links
    fetchTaskLinks,
    addTaskLink,
    updateTaskLink,
    deleteTaskLink,
    // Attachments
    fetchTaskAttachments,
    addTaskAttachments,
    getTaskAttachment,
    deleteTaskAttachment,
    patchTaskAttachment,
  };
};

export default useTasks;
